import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DataTable, SortConfig, TableColumn } from '../../../../ui';
import { JobOpeningList, sortJobOpening } from '../../../../utils/types/JobOpenings';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { IColumn, IJobOpeningRow } from '../interfaces';
import { JobOpeningsEmptyState } from './JobOpeningsEmptyState';

export interface JobOpeningsTableProps {
  data: JobOpeningList[];
  columns: IColumn<IJobOpeningRow>[];
  loading: boolean;
  hasFetchedOnce: boolean;
  totalPages: number;
  pageNumber: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sort: sortJobOpening;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  onRowClick: (id: string) => void;
  isFiltersApplied: boolean;
  onClearFilters: () => void;
}

export const JobOpeningsTable = ({
  data,
  columns,
  loading,
  hasFetchedOnce,
  totalPages,
  pageNumber,
  totalRecords,
  onPageChange,
  sort,
  onSortChange,
  onRowClick,
  isFiltersApplied,
  onClearFilters,
}: JobOpeningsTableProps) => {
  const rows: IJobOpeningRow[] = useMemo(() => {
    return data.map((job) => ({
      id: job.id,
      jobTitle: job.jobTitle,
      jobReference: job.jobReference,
      hiringManager: job.hiringManager,
      internalRecruiter: job.internalRecruiter,
      applicants: job.applicants,
      creationDate: job.creationDate,
      expiryDate: job.expiryDate,
      tags: job.tags || [],
    }));
  }, [data]);

  const tableColumns: TableColumn<IJobOpeningRow>[] = useMemo(() => {
    return columns.map((column) => ({
      key: column.key,
      label: column.label,
      width: column.width,
      align: column.align,
      disableSort: !column.sortable,
      render: (row: IJobOpeningRow) =>
        column.render ? column.render(row) : (row as any)[column.key] || '-',
    }));
  }, [columns]);

  const sortConfig: SortConfig[] = useMemo(() => {
    return Object.entries(sort)
      .filter(([_, direction]) => Boolean(direction))
      .map(([key, direction]) => ({
        key,
        direction: (direction as 'asc' | 'desc') || 'desc',
      }));
  }, [sort]);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <DataTable<IJobOpeningRow>
        columns={tableColumns}
        data={rows}
        isLoading={loading || !hasFetchedOnce}
        rowsPerPage={DEFAULT_PAGE_SIZE}
        currentPage={pageNumber}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
        onClickRow={(row) => onRowClick(row.id)}
        emptyContent={
          <JobOpeningsEmptyState
            isFiltersApplied={isFiltersApplied}
            onClearFilters={onClearFilters}
          />
        }
      />
    </Box>
  );
};
